// ============================================================================
// PRE-LITIGATION SERVICE (Phase 7)
// ============================================================================
// Wraps the /api/v1/PreLitigation surface. Listings transform the backend's
// PagedResult<T> → frontend PaginatedResponse<T> per the mandatory pattern.
// Action endpoints (claim/log_action/etc.) return ApiResponse<T>.

import { get, post, put } from '../client/axiosClient';
import { preLitigationEndpoints, PRELIT_BASE_PATH } from '../endpoints/preLitigation.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse, PaginatedResponse, ID } from '../types';
import type {
  PreLitCase,
  PreLitCaseListItem,
  PreLitCaseSearchRequest,
  EligibleAccount,
  EligiblePoolSearchRequest,
  RecomputePoolRequest,
  BulkPoolMembershipResult,
  AgentAction,
  Commitment,
  ClaimCaseRequest,
  AssignAgentRequest,
  LogAgentActionRequest,
  CaptureCommitmentRequest,
  ResolveCommitmentRequest,
  EscalateRequest,
  ManualCloseRequest,
  ClosePreLitCaseRequest,
  PreLitClosureReason,
  ClosedCaseReviewRow,
  ClosedCasesReviewSearchRequest,
  ConfirmClosureReviewRequest,
  ReopenPreLitCaseRequest,
  ReceiptRow,
  ReceiptsSearchRequest,
  VerifyReceiptRequest,
} from '../types/preLitigation.types';

class PreLitigationService {
  // --------------------------------------------------------------------------
  // POOL
  // --------------------------------------------------------------------------

  async getPool(params?: EligiblePoolSearchRequest): Promise<PaginatedResponse<EligibleAccount>> {
    const endpoint = params
      ? buildEndpointWithQuery(`${PRELIT_BASE_PATH}/pool`, params as Record<string, any>)
      : preLitigationEndpoints.pool();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 25,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async recomputePool(
    request: RecomputePoolRequest = {},
  ): Promise<ApiResponse<BulkPoolMembershipResult>> {
    const response = await post<ApiResponse<BulkPoolMembershipResult>>(
      preLitigationEndpoints.recomputePool(),
      request,
    );
    return response.data;
  }

  // --------------------------------------------------------------------------
  // CASES — list and detail
  // --------------------------------------------------------------------------

  async getCases(
    params?: PreLitCaseSearchRequest,
  ): Promise<PaginatedResponse<PreLitCaseListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(`${PRELIT_BASE_PATH}/cases`, params as Record<string, any>)
      : preLitigationEndpoints.cases();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 25,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<PreLitCase>> {
    const response = await get<ApiResponse<PreLitCase>>(preLitigationEndpoints.getById(id));
    return response.data;
  }

  async getActions(id: ID): Promise<ApiResponse<AgentAction[]>> {
    const response = await get<ApiResponse<AgentAction[]>>(preLitigationEndpoints.caseActions(id));
    return response.data;
  }

  async getCommitments(id: ID): Promise<ApiResponse<Commitment[]>> {
    const response = await get<ApiResponse<Commitment[]>>(preLitigationEndpoints.caseCommitments(id));
    return response.data;
  }

  // --------------------------------------------------------------------------
  // ACTIONS (modal payloads)
  // --------------------------------------------------------------------------

  async claim(id: ID, request: ClaimCaseRequest = {}): Promise<ApiResponse<PreLitCase>> {
    const response = await post<ApiResponse<PreLitCase>>(
      preLitigationEndpoints.claim(id),
      request,
    );
    return response.data;
  }

  async assign(id: ID, request: AssignAgentRequest): Promise<ApiResponse<PreLitCase>> {
    const response = await post<ApiResponse<PreLitCase>>(
      preLitigationEndpoints.assign(id),
      request,
    );
    return response.data;
  }

  async logAction(id: ID, request: LogAgentActionRequest): Promise<ApiResponse<AgentAction>> {
    const response = await post<ApiResponse<AgentAction>>(
      preLitigationEndpoints.logAction(id),
      request,
    );
    return response.data;
  }

  async captureCommitment(
    id: ID,
    request: CaptureCommitmentRequest,
  ): Promise<ApiResponse<Commitment>> {
    const response = await post<ApiResponse<Commitment>>(
      preLitigationEndpoints.captureCommitment(id),
      request,
    );
    return response.data;
  }

  async resolveCommitment(
    commitmentId: ID,
    request: ResolveCommitmentRequest,
  ): Promise<ApiResponse<Commitment>> {
    const response = await put<ApiResponse<Commitment>>(
      preLitigationEndpoints.resolveCommitment(commitmentId),
      request,
    );
    return response.data;
  }

  async escalate(id: ID, request: EscalateRequest): Promise<ApiResponse<PreLitCase>> {
    const response = await post<ApiResponse<PreLitCase>>(
      preLitigationEndpoints.escalate(id),
      request,
    );
    return response.data;
  }

  async manualClose(id: ID, request: ManualCloseRequest): Promise<ApiResponse<PreLitCase>> {
    const response = await post<ApiResponse<PreLitCase>>(
      preLitigationEndpoints.manualClose(id),
      request,
    );
    return response.data;
  }

  async closeCase(id: ID, request: ClosePreLitCaseRequest): Promise<ApiResponse<PreLitCase>> {
    const response = await post<ApiResponse<PreLitCase>>(
      preLitigationEndpoints.closeCase(id),
      request,
    );
    return response.data;
  }

  async getClosureReasons(id: ID): Promise<PreLitClosureReason[]> {
    const response = await get<ApiResponse<PreLitClosureReason[]>>(
      preLitigationEndpoints.closureReasons(id),
    );
    return response.data.data ?? [];
  }

  /**
   * Returns users whose geographic footprint covers the given branch AND who
   * hold the requested role. Drives the AssignAgent / Reassign dropdown.
   * Backend: GET /PreLitigation/agents-by-footprint
   */
  async getAgentsByFootprint(
    branchId: ID,
    roleCode = 'recsuit_field_agent',
  ): Promise<ApiResponse<Array<{ userId: ID; displayName: string; email?: string | null; isActive?: boolean; managerId?: ID | null }>>> {
    const response = await get<ApiResponse<any>>(
      preLitigationEndpoints.agentsByFootprint(branchId, roleCode),
    );
    return response.data;
  }

  // ==========================================================================
  // Closed Cases Review worksheet — supervisor confirms / reopens closed cases
  // ==========================================================================

  async getClosedCases(
    params: ClosedCasesReviewSearchRequest,
  ): Promise<PaginatedResponse<ClosedCaseReviewRow>> {
    const endpoint = buildEndpointWithQuery(
      `${PRELIT_BASE_PATH}/closed-cases`,
      params as unknown as Record<string, unknown>,
    );
    const response = await get<any>(endpoint);
    const raw = response.data;
    const paged = raw.data ?? {};
    return {
      success: raw.success,
      message: raw.message,
      data: paged.items ?? [],
      pagination: {
        currentPage: paged.page ?? 1,
        pageSize: paged.pageSize ?? 25,
        totalPages: paged.totalPages ?? 0,
        totalItems: paged.totalCount ?? 0,
        hasNextPage: paged.hasNext ?? false,
        hasPreviousPage: paged.hasPrevious ?? false,
      },
    };
  }

  async confirmClosureReview(
    id: ID,
    request: ConfirmClosureReviewRequest,
  ): Promise<ApiResponse<PreLitCase>> {
    const response = await post<ApiResponse<PreLitCase>>(
      preLitigationEndpoints.confirmClosureReview(id),
      request,
    );
    return response.data;
  }

  async reopen(
    id: ID,
    request: ReopenPreLitCaseRequest,
  ): Promise<ApiResponse<PreLitCase>> {
    const response = await post<ApiResponse<PreLitCase>>(
      preLitigationEndpoints.reopen(id),
      request,
    );
    return response.data;
  }

  // ==========================================================================
  // Receipts worksheet — branch verifies honoured commitments
  // ==========================================================================

  async getReceipts(
    params: ReceiptsSearchRequest,
  ): Promise<PaginatedResponse<ReceiptRow>> {
    const endpoint = buildEndpointWithQuery(
      `${PRELIT_BASE_PATH}/receipts`,
      params as unknown as Record<string, unknown>,
    );
    const response = await get<any>(endpoint);
    const raw = response.data;
    const paged = raw.data ?? {};
    return {
      success: raw.success,
      message: raw.message,
      data: paged.items ?? [],
      pagination: {
        currentPage: paged.page ?? 1,
        pageSize: paged.pageSize ?? 25,
        totalPages: paged.totalPages ?? 0,
        totalItems: paged.totalCount ?? 0,
        hasNextPage: paged.hasNext ?? false,
        hasPreviousPage: paged.hasPrevious ?? false,
      },
    };
  }

  async verifyReceipt(
    commitmentId: ID,
    request: VerifyReceiptRequest,
  ): Promise<ApiResponse<ReceiptRow>> {
    const response = await post<ApiResponse<ReceiptRow>>(
      preLitigationEndpoints.verifyReceipt(commitmentId),
      request,
    );
    return response.data;
  }
}

export const preLitigationService = new PreLitigationService();
export default preLitigationService;
